import Color from '../constants/Color';
import { formatTenths } from '../util';

/**
 * Enough distinct colours to tell one client's work from the next in a chart.
 * Accent is left out, as it is the colour of every button on the page.
 */
const CHART_COLORS = [
  Color.blue,
  Color.turquoise,
  Color.yellow,
  Color.green,
  Color.blueGrey,
  Color.link,
];

/**
 * A colour per client, fixed by their position in the list so a client keeps
 * the same one everywhere it is charted.
 */
export function getChartColors(
  companies: ReadonlyArray<{ id: string }>
): Record<string, string> {
  return Object.fromEntries(
    companies.map((company, i) => [
      company.id,
      CHART_COLORS[i % CHART_COLORS.length],
    ])
  );
}

export function compareCompanies(
  a: {
    name: string;
  },
  b: {
    name: string;
  }
): number {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
}

/**
 * The line under a client's name on the Projects page: what they are on, how
 * much of it they buy, and how much is open right now.
 */
export function getProjectsMeta(
  company: { retainer: number | null },
  numOpenTickets: number
): string {
  return [
    company.retainer !== null ? 'Maintenance' : null,
    numOpenTickets === 0
      ? 'no open tickets'
      : numOpenTickets === 1
      ? '1 open ticket'
      : `${numOpenTickets} open tickets`,
  ]
    .filter((part) => part !== null)
    .join(' · ');
}

const MINUTES_PER_HOUR = 60;

/**
 * Hours as they read beside a bar, keeping a part-hour rather than hiding it.
 */
export function formatHours(minutes: number): string {
  return formatTenths(minutes / MINUTES_PER_HOUR);
}

type Maintenance = {
  /** How much of the allocation has gone, capped at 1. */
  progress: number;
  label: string;
};

/**
 * The maintenance allocation and how much of it this period has gone, or null
 * for a client which buys none and so has nothing to weigh against.
 */
export function getMaintenance(
  company: {
    maintenanceHoursMonthly: number | null;
    maintenanceHoursWeekly: number | null;
  },
  minutesLogged: number | null
): Maintenance | null {
  const { maintenanceHoursMonthly, maintenanceHoursWeekly } = company;
  const hours = maintenanceHoursMonthly ?? maintenanceHoursWeekly;

  if (hours === null || hours <= 0 || minutesLogged === null) {
    return null;
  }

  const period = maintenanceHoursMonthly !== null ? 'this month' : 'this week';
  const used = minutesLogged / MINUTES_PER_HOUR;

  return {
    progress: Math.min(used / hours, 1),
    label: `${formatHours(minutesLogged)} of ${hours} hours ${period}`,
  };
}
